import { Card, useModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { CardActionArea, CardContent } from "@mui/material";
import { useWorkerProfilePicture } from "@src/appV2/Agents/api/useWorkerProfilePicture";
import { type WorkplaceCommentType } from "@src/appV2/redesign/Workplace/Review/api/useWorkplaceComments";
import { useDelayedWorkplaceReviewFlag } from "@src/appV2/redesign/Workplace/Review/useDelayedWorkplaceReviewFlag";
import { parseISO } from "date-fns";

import { CardDelayedLayout } from "./CardDelayedLayout";
import { CardStandardLayout } from "./CardStandardLayout";
import { ConfirmAnonymizeCommentBottomSheet } from "./ConfirmAnonymizeCommentBottomSheet";
import { ConfirmRemoveCommentBottomSheet } from "./ConfirmRemoveCommentBottomSheet";

interface WorkplaceReviewCommentCardProps {
  comment: WorkplaceCommentType;
  workplaceId: string;
  viewerIsAuthor: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  onLikeChanged: (comment: WorkplaceCommentType) => void;
  onAnonymized: (comment: WorkplaceCommentType) => void;
  onRemoved: (commentId: string) => void;
}

export function WorkplaceReviewCommentCard(props: WorkplaceReviewCommentCardProps) {
  const { comment, workplaceId, onClick, onLikeChanged, viewerIsAuthor, onAnonymized, onRemoved } =
    props;
  const { createdAt, workerInfo } = comment.attributes;
  const { id: workerId } = workerInfo;

  const { useDelayedReviewUI } = useDelayedWorkplaceReviewFlag();

  const anonModalState = useModalState();
  const removeModalState = useModalState();
  const { data: profilePicture, isInitialLoading: isProfilePictureLoading } =
    useWorkerProfilePicture(String(workerId), {
      enabled: isDefined(workerId),
    });

  const layoutProps = {
    comment,
    workplaceId,
    viewerIsAuthor,
    profilePictureUrl: profilePicture?.distribution_url,
    isLoadingProfilePicture: isProfilePictureLoading,
    createdAt: parseISO(createdAt),
    onLikeChanged,
    onAnonymize: () => {
      anonModalState.openModal();
    },
    onRemove: () => {
      removeModalState.openModal();
    },
  };

  return (
    <>
      <Card outlined variant="tertiary">
        <CardActionArea onClick={onClick}>
          <CardContent
            sx={{
              padding: 7,
              height: "100%",
              minHeight: "11rem",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            {useDelayedReviewUI ? (
              <CardDelayedLayout {...layoutProps} />
            ) : (
              <CardStandardLayout {...layoutProps} />
            )}
          </CardContent>
        </CardActionArea>
      </Card>
      {viewerIsAuthor && (
        <ConfirmAnonymizeCommentBottomSheet
          modalState={anonModalState}
          commentId={comment.id}
          workplaceId={workplaceId}
          onAnonymized={onAnonymized}
        />
      )}
      {viewerIsAuthor && (
        <ConfirmRemoveCommentBottomSheet
          modalState={removeModalState}
          commentId={comment.id}
          workplaceId={workplaceId}
          onRemoved={() => {
            onRemoved(comment.id);
          }}
        />
      )}
    </>
  );
}
